<template>
    <div class="dialog draggable-item bg-light light center text-small overflow w-100-sm w-100-md w-75-lg  w-50-xl" v-cloak>

        <DialogTitle :title="serviceAccount.id == null ? $t('Add Service Account') : serviceAccount.name" :defaultTitle="serviceAccount.id == null ? $t('Add Service Account') : $t('Edit/Test Service Account')" :subTitle="serviceAccount.type_name" @closeDialog="$root.$emit('CLOSESERVICEACCOUNTDIALOG')" />

        <!--FINE DIALOG TITLE-->

        <div class="dialog-content overflow bg-light">

            <div v-once class="form-group">
                <!--ISTRUZIONI AMAZON S3-->
                <div v-if="serviceAccount.type == $ONEServiceType.Cloud_AmazonS3Storage" class="text-secondary border bg-white border-1 bd-black p-4">
                    <h5 class="fg-cobalt">{{ $t("Retrieve {0} credentials", { 0: serviceAccount.type_name }) }}</h5>
                    <ol>
                        <li>
                            <span>{{ $t("Log in") }} </span>
                            <a target="_blank" href="https://aws.amazon.com/">{{ $t("this link") }}</a>
                        </li>
                        <li>{{ $t("Click on the name of the user used in the top right-hand corner, then click on 'Security Credentials'") }}</li>
                        <li>{{ $t("Generate a new secret key") }}</li>
                        <li>{{ $t("Fill in the following fields with the information displayed") }}</li>
                    </ol>

                </div>

                <!--ISTRUZIONI IPERIUS S3 STORAGE-->
                <div v-if="serviceAccount.type == $ONEServiceType.Cloud_IperiusS3Storage" class="text-secondary border bg-white border-1 bd-black p-4">
                    <h5 class="fg-cobalt">{{ $t("Retrieve {0} credentials", { 0: serviceAccount.type_name }) }}</h5>
                    <p>{{ $t("Credentials were provided at the time of purchase, so check your inbox") }}</p>
                </div>

                <!--ISTRUZIONI AZURE BLOB/FILE-->
                <div v-if="[$ONEServiceType.Cloud_AzureBlob, $ONEServiceType.Cloud_AzureFile].includes(serviceAccount.type)" class="text-secondary border bg-white border-1 bd-black p-4">
                    <h5 class="fg-cobalt">{{ $t("Retrieve {0} credentials", { 0: serviceAccount.type_name }) }}</h5>
                    <ol>
                        <li>
                            <span>{{ $t("Log in") }} </span>
                            <a target="_blank" href="https://portal.azure.com/#home">{{ $t("this link") }}</a>
                        </li>
                        <li>{{ $t("Click on the 'Storage Account' resource you want to use") }}</li>
                        <li>{{ $t("From the left-hand menu, select 'Security and Network', then 'Access Keys'") }}</li>
                        <li>{{ $t("Fill in the following fields with the information displayed") }}</li>
                    </ol>
                </div>

                <!--ISTRUZIONI DROPBOX-->
                <div v-if="serviceAccount.type == $ONEServiceType.Cloud_Dropbox" class="text-secondary border bg-white border-1 bd-black p-4">
                    <h5 class="fg-cobalt">{{ $t("Retrieve {0} credentials", { 0: serviceAccount.type_name }) }}</h5>
                    <ol>
                        <li>
                            <span>{{ $t("Log in") }} </span>
                            <a target="_blank" href="https://www.dropbox.com/developers/apps">{{ $t("this link") }}</a>
                        </li>
                        <li>{{ $t("Create or select the application you wish to use") }}</li>
                        <li>
                            <span>{{ $t("Add, if not already present, the redirct URI {0}", { 0: redirectURI }) }} </span>
                            <span class="badge mif-copy mr-2 p-0" @click="$root.copy(redirectURI)"></span>
                        </li>
                        <li>{{ $t("Fill in the following fields with the information displayed") }}</li>
                    </ol>
                </div>

                <!--ISTRUZIONI GOOGLE DRIVE-->
                <div v-if="serviceAccount.type == $ONEServiceType.Cloud_GoogleDrive" class="text-secondary border bg-white border-1 bd-black p-4">
                    <h5 class="fg-cobalt">{{ $t("Retrieve {0} credentials", { 0: serviceAccount.type_name }) }}</h5>
                    <ol>
                        <li>
                            <a target="_blank" href="https://console.cloud.google.com/">{{ $t("Open Google Cloud Console") }}</a>
                            <span> {{ $t("and create a new project or select an existing project to use with Iperius ONE.") }}</span>
                        </li>
                        <li>{{ $t("Open API & Services > Library, search for Google Drive API, and click Enable.") }}</li>
                        <li>{{ $t("Open Google Auth Platform. Under Branding, configure the application name, user support email, and contact email. Then set the user type to External under Audience.") }}</li>
                        <li>{{ $t("Under Audience, add the Google account you will use for authentication to Test users.") }}</li>
                        <li>
                            <span>{{ $t("Open Data Access and add the following scopes:") }}</span>
                            <ul>
                                <li><code>https://www.googleapis.com/auth/drive.file</code></li>
                                <li><code>https://www.googleapis.com/auth/drive.appdata</code></li>
                                <li><code>https://www.googleapis.com/auth/drive.install</code></li>
                            </ul>
                        </li>
                        <li>{{ $t("Open Clients, click Create Client, select Web application as the application type, and create an OAuth 2.0 Client ID.") }}</li>
                        <li>
                            <span>{{ $t("Under Authorized redirect URIs, add exactly the following address:") }} </span>
                            <code>{{ redirectURI }}</code>
                            <span class="badge mif-copy mr-2 p-0" @click="$root.copy(redirectURI)"></span>
                        </li>
                        <li>{{ $t("Complete the OAuth client creation and copy the Client ID and Client Secret into the corresponding fields below. Finally, click Test account and complete the authorization requested by Google.") }}</li>
                    </ol>
                </div>
            </div>

            <div class="form-group">
                <label>{{ $t("Name") }}</label>
                <input data-role="input" type="text" v-model.trim="serviceAccount.name" maxlength="50" :placeholder="$t('enter name')" data-prepend="<span class='mif-pencil'></span>" />

            </div>

            <!--S3 PROVIDER-->
            <!--Mostro la scelta del provider solo in creazione-->
            <div v-if="[$ONEServiceType.Cloud_S3CompatibleStorage].includes(serviceAccount.type) && serviceAccount.id == null" class="form-group">
                <label>{{ $t("Provider") }}</label>
                <select class="w-100" data-role="select" data-filter="true" v-model="s3Provider" @change="onChangeS3Provider()">
                    <option :value="null" key="Other provider">{{ $t("Other provider") }}</option>
                    <option v-for="(provObj, provName) in s3Providers" :value="provObj" :key="provName">
                        {{ provName }}
                    </option>
                </select>

            </div>

            <div>
                <h6 class="border-bottom pb-1 mt-3 text-bold">{{ $t("Credentials") }}</h6>
            </div>
            <!--Client ID ----------------------------------------------------------------------->
            <div v-if="needClientIDandClientSecret" class="form-group">
                <label v-if="serviceAccount.type.isCloudS3()">Access Key</label>
                <label v-else-if="[$ONEServiceType.Cloud_AzureBlob, $ONEServiceType.Cloud_AzureFile].includes(serviceAccount.type)">Account Name</label>
                <label v-else-if="[$ONEServiceType.Cloud_Dropbox].includes(serviceAccount.type)">App Key</label>
                <label v-else>Client ID</label>
                <input data-role="input" v-model.trim="serviceAccount.client_id" type="text" v-no-autocomplete="'mif-key'" />

            </div>

            <!--Client Secret ----------------------------------------------------------------------->
            <div v-if="needClientIDandClientSecret" class="form-group">
                <label v-if="serviceAccount.type.isCloudS3()">Secret Key</label>
                <label v-else-if="[$ONEServiceType.Cloud_AzureBlob, $ONEServiceType.Cloud_AzureFile].includes(serviceAccount.type)">Access
                    Key</label>
                <label v-else-if="[$ONEServiceType.Cloud_Dropbox].includes(serviceAccount.type)">App
                    Secret</label>
                <label v-else>Client Secret</label>
                <!--L'accesskey non ha bisogno di essere nascosto-->
                <input v-if="[null, ''].includes(serviceAccount.id) || [$ONEServiceType.Cloud_AzureBlob, $ONEServiceType.Cloud_AzureFile].includes(serviceAccount.type)" class="metro-input" data-role="input" type="password" v-model.trim="serviceAccount.client_secret" v-no-autocomplete="'mif-lock'" />
                <input v-else class="metro-input" data-role="input" type="password" data-reveal-button="false" data-prepend="<span class='mif-lock'></span>" value="**********" disabled />


            </div>

            <!--Region ----------------------------------------------------------------------->
            <div v-if="regions.length > 1" class="form-group">
                <label>{{ $t("Region") }}</label>
                <select v-if="(typeof regions[0]) != 'string'" class="w-33" data-role="select" data-filter="true" v-model="serviceAccount.region" @change="onChangeRegion()">
                    <option v-for="region in regions" :value="region.Url" :key="region.Url">
                        {{ region.Name }}
                    </option>
                </select>
                <select v-else class="w-33" data-role="select" data-filter="true" v-model="serviceAccount.region" @change="onChangeRegion()">
                    <option v-for="region in regions" :value="region" :key="region">
                        {{ region }}
                    </option>
                </select>


            </div>

            <!--URL ----------------------------------------------------------------------->
            <div v-if="[$ONEServiceType.Cloud_S3CompatibleStorage].includes(serviceAccount.type)" class="form-group">
                <label>{{ $t("URL") }}</label>
                <input data-role="input" class="metro-input" type="text" v-model="serviceAccount.url" :disabled="this.serviceAccount.url.contains('REGION')" />

            </div>
            <div v-if="[$ONEServiceType.Cloud_IperiusS3Storage, $ONEServiceType.Cloud_S3CompatibleStorage].includes(serviceAccount.type)" class="form-group">
                <input data-role="checkbox" type="checkbox" :data-caption="$t('Use secure connection (SSL/TLS)')" v-model="serviceAccount.use_ssl" required data-prepend="<span class='mif-lock'></span>" :disabled="serviceAccount.type == $ONEServiceType.Cloud_IperiusS3Storage" />
            </div>
            <div v-if="serviceAccount.type == $ONEServiceType.Cloud_S3CompatibleStorage" class="form-group">
                <input data-role="checkbox" type="checkbox" :data-caption="$t('Use virtual hosting')" v-model="serviceAccount.options.useVirtualHosting" />
            </div>

            <!--App name ----------------------------------------------------------------------->
            <div v-if="serviceAccount.type == $ONEServiceType.Cloud_OneDrive" class="form-group">
                <label>{{ $t("Application Name") }}</label>
                <!--
                    Se il client ID è già valorizzato, significa che è stata aperta la dialog in modifica
                    Nel caso di Onedrive, non devo permettere la modifica del nome dell'applicazione registrata. Sarebbe illogico.
                -->
                <input data-role="input" class="metro-input" type="text" v-model.trim="serviceAccount.options.appName" data-prepend="<span class='mif-lock'></span>" :disabled="serviceAccount.client_id != ''" />

                <input data-role="input" class="metro-input" type="text" v-model.trim="serviceAccount.options.appName" data-prepend="<span class='mif-lock'></span>" :disabled="serviceAccount.client_id != ''" />

            </div>
            <div>
                <h6 class="border-bottom pb-1 mt-3 text-bold">{{ $t("Options") }}</h6>
            </div>
            <div v-if="[$ONEServiceType.Cloud_IperiusS3Storage, $ONEServiceType.Cloud_S3CompatibleStorage].includes(serviceAccount.type)" class="form-group">
                <input data-role="checkbox" type="checkbox" :data-caption="$t('Use secure connection (SSL/TLS)')" v-model="serviceAccount.use_ssl" required data-prepend="<span class='mif-lock'></span>" :disabled="serviceAccount.type == $ONEServiceType.Cloud_IperiusS3Storage" />
            </div>
            <div v-if="serviceAccount.type == $ONEServiceType.Cloud_S3CompatibleStorage" class="form-group">
                <input data-role="checkbox" type="checkbox" :data-caption="$t('Use virtual hosting')" v-model="serviceAccount.options.useVirtualHosting" />
            </div>

            <!--Signature version---------------------------------------------------->
            <div v-if="[$ONEServiceType.Cloud_S3CompatibleStorage].includes(serviceAccount.type)" class="form-group">
                <label>{{ $t("Signature version") }}</label>
                <select class="select w-25" v-model="serviceAccount.options.signatureversion">
                    <option :value="2" :key="2">2</option>
                    <option :value="4" :key="4">4</option>
                </select>
            </div>

            <div v-if="showObjectLockEnabled">
                <h6 class="border-bottom pb-1 mt-3 text-bold">{{ $t("Object lock") }}</h6>
            </div>

            <!--Object lock- enable ---------------------------------------------------->
            <div v-if="showObjectLockEnabled" class="form-group d-flex">
                <input data-role="checkbox" type="checkbox" v-model="serviceAccount.options.objLock_enabled" :true-value="1" :false-value="0" />
                <div>
                    <label>
                        <span>{{ $t("Enable Object Lock / Immutability") }}</span><br />
                        <small>{{ $t("Protects uploaded objects by preventing them from being deleted until the configured expiry date. Requires an Object Lock-compatible bucket.") }}</small>
                    </label>
                </div>
            </div>

            <!--Object lock- mode ---------------------------------------------------->
            <div v-if="showObjectLockMode" class="form-group">
                <label>{{ $t("Protection mode") }}</label>
                <div class="mt-2" :style="objectLockEnabled ? null : { opacity: 0.55 }">
                    <input v-if="objectLockModes.includes('GOVERNANCE')" data-role="radio" type="radio" name="objectLockMode" value="GOVERNANCE" v-model="serviceAccount.options.objLock_mode" :data-caption="$t('Governance')" :disabled="!objectLockEnabled" />
                    <p v-if="objectLockModes.includes('GOVERNANCE')" class="text-secondary ml-8 mb-3">
                        {{ $t("Recommended protection for backups. Prevents deletion by standard users, but can be bypassed by authorised administrators with specific permissions.") }}
                    </p>

                    <input v-if="objectLockModes.includes('COMPLIANCE')" data-role="radio" type="radio" name="objectLockMode" value="COMPLIANCE" v-model="serviceAccount.options.objLock_mode" :data-caption="$t('Compliance')" :disabled="!objectLockEnabled" />
                    <p v-if="objectLockModes.includes('COMPLIANCE')" class="text-secondary ml-8 mb-3">
                        {{ $t("Strict WORM protection. No user, not even the administrator/root, can remove or reduce this protection before the expiry date.") }}
                    </p>

                    <p class="text-secondary mb-2">
                        {{ $t("Governance is recommended in most cases. Compliance should only be used when unalterable retention is required for regulatory or business reasons.") }}
                    </p>
                    <p class="text-secondary mb-0">
                        <strong>{{ $t("Please note:") }}</strong>
                        {{ $t("in Compliance mode, objects cannot be deleted or unlocked until the retention period has expired. Check the set retention period carefully.") }}
                    </p>

                    <div v-if="showObjectLockRetain" class="mt-4">
                        <label>{{ $t("Retention period (days)") }}</label>
                        <input data-role="input" class="metro-input w-25" type="number" min="1" step="1" v-model.number="serviceAccount.options.objLock_retainDays" :disabled="!objectLockEnabled" />
                    </div>
                </div>
            </div>

            <div v-if="(serviceAccount.options.authCode != undefined && serviceAccount.options.authCode != null) || googledrive_url == ''">
                <h6 class="border-bottom pb-1 mt-3 text-bold">{{ $t("Authentication") }}</h6>
            </div>

            <!--DATI EXTRA PER AUTENTICAZIONE ------------------------------------------------------->
            <!--Se durante l'autenticazione a Dropbox è necessaria l'authorization code, verrà mostrata quasta parte-->
            <div class="form-group d-flex flex-align-center flex-justify-center flex-column" v-if="serviceAccount.options.authCode != undefined && serviceAccount.options.authCode != null">
                <h5 class="mb-5">{{ $t("Access code") }}</h5>
                <input data-role="input" class="metro-input w-50" type="password" v-model.trim="serviceAccount.options.authCode" :placeholder="$t('Enter access code')" data-prepend="<span class='mif-lock'></span>" />
                <button class="button bg-amber" @click="test" :disabled="[undefined, ''].includes(serviceAccount.options.authCode)"> {{ $t("Confirm access code") }} </button>
            </div>

            <!--Se durante l'autenticazione a Google Drive è necessaria l'abilitazione di Google Drive API nella console di Google-->
            <div class="form-group" v-if="googledrive_url == ''">
                {{ $t("Click on the following URL and enable the Google Drive API, then try the test again") }}:<br />
                <a :href="googledrive_url" target="_blank">{{ googledrive_url }}</a>
            </div>
        </div>

        <!-------------------------------TEST ACCOUNT----------------------------------------->
        <div class="dialog-actions">
            <ClientMessageView :commands="['testserviceaccount', 'cloudmethods']" ref="CM_SA" />
            <!--Durante l'autenticazione a Onedrive, verrà mostrato un messaggio che indica il corrente step -->
            <button class="button bg-amber" @click="test" :disabled="!enableTest" v-disable-tooltip:disabledTest_message> {{ $t("Test account") }} </button>
            <button class="button secondary" :disabled="!enableSave" v-disable-tooltip="$t('You can save the account only if the account test is successful.')" @click="saveServiceAccount(serviceAccount)">{{ $t("Save") }}</button>
        </div>

    </div>
</template>

<script>
import { isValid, waitSeconds } from "../../../utils/utilitiesmodule";
import DialogTitle from "../../utils/DialogTitle.vue";

import { draggableMixin } from '../../../mixins/draggable-mixin'
import ClientMessageView from "../view/ClientMessageView.vue";
import { ClientMessage } from "../../../utils/objects";
import { S3Helper } from "../../../utils/serviceAccountsUtils/clientsConfig/s3Regions";
import { saveServiceAccount, testServiceAccount } from "../../../utils/entityMethods/serviceAccount";


export default {

    mixins: [draggableMixin],
    name: "CloudServiceAccount",
    components: {
        "DialogTitle": DialogTitle,
        ClientMessageView
    },

    props: {
        serviceAccount: Object,
    },

    data() {
        return {
            regions: [],

            tested: this.$ResultValue.NeverExecuted,
            disabledTest_message: "",

            googledrive_url: null,// caso particolare di Google Drive,

            redirectURI: window.location.origin,

            s3Providers: null,
            s3Provider: null,
            comp: 0
        }
    },
    computed: {
        needClientIDandClientSecret() {
            switch (this.serviceAccount.type) {
                case this.$ONEServiceType.Cloud_GoogleDrive:
                case this.$ONEServiceType.Cloud_S3CompatibleStorage:
                case this.$ONEServiceType.Cloud_IperiusS3Storage:
                case this.$ONEServiceType.Cloud_AmazonS3Storage:
                case this.$ONEServiceType.Cloud_Dropbox:
                case this.$ONEServiceType.Cloud_AzureBlob:
                case this.$ONEServiceType.Cloud_AzureFile:
                    return true;
                default:
                    return false;
            }
        },
        selectedS3Provider() {
            return this.serviceAccount.type == this.$ONEServiceType.Cloud_S3CompatibleStorage ? this.s3Provider : null;
        },
        objectLockModes() {
            if (this.serviceAccount.type == this.$ONEServiceType.Cloud_AmazonS3Storage) {
                return ["GOVERNANCE", "COMPLIANCE"];
            }

            if (this.selectedS3Provider && Array.isArray(this.selectedS3Provider.objLock_mode)) {
                return this.selectedS3Provider.objLock_mode;
            }

            return [];
        },
        showObjectLockEnabled() {
            if (this.serviceAccount.type == this.$ONEServiceType.Cloud_AmazonS3Storage) {
                return true;
            }

            return this.selectedS3Provider != null && this.selectedS3Provider.objLock_enabled != -1;
        },
        showObjectLockMode() {
            return this.objectLockModes.length != 0;
        },
        objectLockEnabled() {
            return this.serviceAccount.options.objLock_enabled == 1;
        },
        objectLockRetainDefault() {
            if (!this.selectedS3Provider) {
                return 0;
            }

            return this.selectedS3Provider.objLock_retain != undefined ? this.selectedS3Provider.objLock_retain : this.selectedS3Provider.objLock_retainDays;
        },
        showObjectLockRetain() {
            return this.showObjectLockMode && this.objectLockRetainDefault != 0;
        },
        allInputCompleted() {
            if (this.serviceAccount.name == "") {
                this.setClientMessage(ClientMessage.info("Insert a name"));
                return false;
            }

            if (this.needClientIDandClientSecret && (this.serviceAccount.client_id == "" || this.serviceAccount.client_secret == "")) {
                this.setClientMessage(ClientMessage.info("Enter your credentials"));
                return false;
            }

            switch (this.serviceAccount.type) {
                case this.$ONEServiceType.Cloud_S3CompatibleStorage:

                    //Altro provider ma url vuoto
                    if (!this.s3Provider && this.serviceAccount.url == "") {
                        this.setClientMessage(ClientMessage.info("Enter a valid URL"));
                        return false;
                    }

                    if (this.serviceAccount.url.contains("REGION")) {
                        this.setClientMessage(ClientMessage.info("Select a region"));
                        return false;
                    }

                    //Provider e url che deve essere modificato (contiene REGION, BUCKET e/o ACCOUNTID)
                    if (this.s3Provider && /\p{Lu}/u.test(this.s3Provider.url) && /\p{Lu}/u.test(this.serviceAccount.url)) {
                        this.setClientMessage(ClientMessage.info("Edit the URL with your details"));
                        return false;
                    }
                    return true;

                case this.$ONEServiceType.Cloud_IperiusS3Storage:
                    return this.serviceAccount.url != "";

                case this.$ONEServiceType.Cloud_AmazonS3Storage:
                    return this.serviceAccount.region != "";

                case this.$ONEServiceType.Cloud_OneDrive:
                    return isValid(this.serviceAccount.options.appName);
            }
            return true;
        },
        enableTest() {
            if (this.tested == this.$ResultValue.Running) {
                return false;
            }

            if (!this.allInputCompleted) {
                return false;
            }

            switch (this.tested) {

                case this.$ResultValue.NeverExecuted:
                    if (this.serviceAccount.id != null) {
                        this.setClientMessage(null);
                    } else {
                        this.setClientMessage(ClientMessage.info_translated(this.$t('You can save the account only if the account test is successful.')));
                    }
                    return true;

                case this.$ResultValue.Running:
                    this.disabledTest_message = "";
                    return false;

                case this.$ResultValue.OK:
                    this.disabledTest_message = this.$t("Service account already validated");
                    return false;
                case this.$ResultValue.Error:

                    if (this.serviceAccount.type == this.$ONEServiceType.Cloud_Dropbox && this.serviceAccount.options.authCode != undefined) {
                        this.setClientMessage(ClientMessage.info_translated(this.$t("Enter the access code displayed after logging in to Dropbox and repeat the test.")));
                        return true;
                    }

                    if (this.serviceAccount.id == null) {
                        this.disabledTest_message = this.$t("You can save the account only if the account test is successful.");
                        this.setClientMessage(ClientMessage.info_translated(this.disabledTest_message));
                    } else {
                        this.disabledTest_message = "";
                    }

                    return true;
            }
        },
        enableSave() {
            if (!this.allInputCompleted)
                return false;

            if (this.tested == this.$ResultValue.OK)
                return true;

            if (this.tested == this.$ResultValue.Running)
                return false;

            return this.serviceAccount.id != null
        },
    },
    created() {
        this.initObjectLockOptions();

        if (this.serviceAccount.type == this.$ONEServiceType.Cloud_AmazonS3Storage) {
            let self = this;
            this.$api.getAmazonRegions().then(regions => {
                self.regions = regions;
            });
            return;
        }

        if (this.serviceAccount.type == this.$ONEServiceType.Cloud_S3CompatibleStorage) {
            this.s3Providers = S3Helper.providers;
            if (!isValid(this.serviceAccount.id)) {
                return;
            }
            //Se sono in edit e riconosco un provider, lo imposto
            var provideName = S3Helper.detectS3Provider(this.serviceAccount.url);
            if (!isValid(provideName)) {
                return;
            }
            this.s3Provider = this.s3Providers[provideName];
            this.regions.clear();
            if (this.s3Provider.regions.length > 1) {
                waitSeconds(0.1).then(() => this.regions.update(this.s3Provider.regions));
            }
            this.initObjectLockOptions();

        }
    },

    methods: {
        saveServiceAccount,
        testServiceAccount,

        async test() {
            let self = this;
            this.tested = this.$ResultValue.Running;

            if (self.serviceAccount.type == this.$ONEServiceType.Cloud_IperiusS3Storage) {
                self.serviceAccount.url = "https://{0}-s3.iperiusstorage.com".format(this.serviceAccount.client_id);
            }




            /**
            * {
            *  tested : //bool,
            *  serviceAccount : ,
            *  client .
            * }
            */
            var testResult = await this.testServiceAccount(self.serviceAccount);
            self.tested = testResult.ok ? this.$ResultValue.OK : this.$ResultValue.Error;

            /*                 if (testResult.ok) {
                                break;
                            }
            
                            if (self.serviceAccount.type==this.$ONEServiceType.Cloud_Dropbox&&self.serviceAccount.options.authCode==null) {
                                self.serviceAccount.options.authCode=""; // fa apparire la parte per inserire l'authorization code
                                //this.setClientMessage(ClientMessage.info("Enter the access code displayed after logging in to Dropbox and repeat the test.", null));
                                return;
                            }
                        } */
            self.tested = testResult.ok ? this.$ResultValue.OK : this.$ResultValue.Error;
            try {


                //Caso 1 : il test è andato a buon fine
                if (testResult.ok) {
                    return;
                }

                //Caso 2: Il test non è andato a buon fine e:
                // - è Dropbox
                // - non è stato aggiunto un authorization code
                if (self.serviceAccount.type == this.$ONEServiceType.Cloud_Dropbox && self.serviceAccount.options.authCode == null) {
                    self.serviceAccount.options.authCode = ""; // fa apparire la parte per inserire l'authorization code
                    return;
                }

                //Caso 3 : Il test non è andato a buon fine e:
                // - è Google Drive
                // - il risultato ha restituito un url
                if (self.serviceAccount.type == this.$ONEServiceType.Cloud_GoogleDrive && testResult.url != undefined) {
                    this.googledrive_url = testResult.url;
                    return;
                }
            } finally {
                self.tested = testResult.ok ? this.$ResultValue.OK : this.$ResultValue.Error;
            }
        },


        setClientMessage(clientMessage) {
            this.comp++;

            if (this.$refs.CM_SA) {
                this.$refs.CM_SA.setClientMessage(clientMessage);
            } else {
                waitSeconds(0.2).then(_ => {
                    this.$refs.CM_SA.setClientMessage(clientMessage);
                });
            }
        },

        onChangeS3Provider() {
            if (isValid(this.serviceAccount.id)) {
                return;
            }

            if (!this.s3Provider) {
                if (isValid(this.serviceAccount.id))
                    return;
                this.serviceAccount.region = "";
                this.serviceAccount.url = "";
                this.regions.clear();
                this.serviceAccount.use_ssl = true;
                this.serviceAccount.options.signatureversion = 4;
                this.serviceAccount.options.useVirtualHosting = true;
                this.serviceAccount.options.objLock_enabled = 0;
                this.serviceAccount.options.objLock_mode = "GOVERNANCE";
                this.serviceAccount.options.objLock_retainDays = 0;
                return;
            }

            this.serviceAccount.region = this.s3Provider.region;
            this.serviceAccount.url = this.s3Provider.url;
            this.regions.clear();
            if (this.s3Provider.regions.length > 1) {
                waitSeconds(0.1).then(() => this.regions.update(this.s3Provider.regions));
            }
            this.serviceAccount.use_ssl = this.s3Provider.useSSL;
            this.serviceAccount.options.signatureversion = this.s3Provider.signatureVersion;
            this.serviceAccount.options.useVirtualHosting = this.s3Provider.useVirtualHosting;
            this.serviceAccount.options.objLock_enabled = this.s3Provider.objLock_enabled == 1 ? 1 : 0;
            this.serviceAccount.options.objLock_mode = this.getDefaultObjectLockMode();
            this.serviceAccount.options.objLock_retainDays = this.getDefaultObjectLockRetain();
        },
        onChangeRegion() {
            if (!this.s3Provider || this.s3Provider.regions.length == 1)
                return;
            this.serviceAccount.url = this.s3Provider.url.replace("REGION", this.serviceAccount.region);
        },
        getDefaultObjectLockMode() {
            return this.objectLockModes.includes("GOVERNANCE") ? "GOVERNANCE" : this.objectLockModes[0];
        },
        getDefaultObjectLockRetain() {
            return this.objectLockRetainDefault != undefined ? this.objectLockRetainDefault : 0;
        },
        initObjectLockOptions() {
            if (this.serviceAccount.options.objLock_enabled == undefined) {
                this.$set(this.serviceAccount.options, "objLock_enabled", 0);
            } else if (this.serviceAccount.options.objLock_enabled === true) {
                this.serviceAccount.options.objLock_enabled = 1;
            } else if (this.serviceAccount.options.objLock_enabled === false) {
                this.serviceAccount.options.objLock_enabled = 0;
            }

            if (this.serviceAccount.options.objLock_mode == undefined) {
                this.$set(this.serviceAccount.options, "objLock_mode", "GOVERNANCE");
            }

            if (this.serviceAccount.options.objLock_retainDays == undefined || (this.showObjectLockRetain && this.serviceAccount.options.objLock_retainDays == 0)) {
                this.$set(this.serviceAccount.options, "objLock_retainDays", this.getDefaultObjectLockRetain());
            }
        }
    }
}
</script>
